import { ArrowDown2 } from "iconsax-react-nativejs";
import { useMemo, useState, useRef } from "react";
import {
	View,
	Text,
	Pressable,
	Modal,
	FlatList,
	TextInput,
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";

type DropdownOption = Readonly<{
	label: string;
	value: string;
}>;

type ScDropdownProps = Readonly<{
	options: DropdownOption[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	triggerClassName?: string;
	menuWidth?: number;
	menuMaxHeight?: number;
	/**
	 * Adds a filter box at the top of the menu. Opt-in, because it only earns its
	 * space on long lists — a country list runs to ~200 entries, where scrolling
	 * to find one is the whole problem.
	 */
	searchable?: boolean;
	searchPlaceholder?: string;
	/** Shown when the filter matches nothing. */
	noResultsLabel?: string;
}>;

const ScDropdown = ({ options, value, onChange, placeholder = "Select...", triggerClassName = "py-2.5 md:py-3 bg-chalk", menuWidth, menuMaxHeight = 300, searchable = false, searchPlaceholder = "Search...", noResultsLabel = "No matches" }: ScDropdownProps) => {
	const {themeVars} = useTheme();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const triggerRef = useRef<View>(null);
	const [triggerLayout, setTriggerLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });

	const selected = options.find(o => o.value === value);

	// Matched on the label, which is what the user is reading. Country labels
	// carry a leading flag emoji, so this has to be `includes` rather than a
	// prefix test.
	const visibleOptions = useMemo(() => {
		if (!searchable) return options;
		const term = query.trim().toLowerCase();
		if (!term) return options;
		return options.filter((option) => option.label.toLowerCase().includes(term));
	}, [options, query, searchable]);

	const closeDropdown = () => {
		setOpen(false);
		setQuery("");
	};

	const openDropdown = () => {
		triggerRef.current?.measure((fx, fy, width, height, px, py) => {
			setTriggerLayout({ x: px, y: py + height + 4, width, height });
			setQuery("");
			setOpen(true);
		});
	};

	return (
		<>
			<Pressable
				ref={triggerRef}
				onPress={openDropdown}
				className={`flex-row items-center justify-between px-4 self-stretch md:border md:border-ash-3 rounded-xl min-w-36 hover:bg-ash-3 ${triggerClassName}`}
			>
				<Text numberOfLines={1} className={`text-sm flex-1 mr-1 ${selected ? "text-onyx" : "text-ash-2"}`}>
					{selected?.label ?? placeholder}
				</Text>
				<ArrowDown2 size={18} color="var(--color-ash-2)" />
			</Pressable>

			<Modal visible={open} transparent animationType="none" onRequestClose={closeDropdown}>
				<Pressable style={themeVars as any} className="flex-1" onPress={closeDropdown}>
					{/* Swallows the backdrop press so tapping inside the menu — the
					    search box especially — does not close it. */}
					<Pressable
						onPress={() => {}}
						style={{
							position: "absolute",
							top: triggerLayout.y,
							left: triggerLayout.x,
							width: menuWidth ?? triggerLayout.width,
							maxHeight: menuMaxHeight,
						}}
						className="bg-chalk border border-ash-3 rounded-xl overflow-hidden shadow-sm p-1"
					>
						{searchable && (
							<TextInput
								value={query}
								onChangeText={setQuery}
								placeholder={searchPlaceholder}
								placeholderTextColor={themeVars["--color-ash-2"] as string}
								autoFocus
								className="mx-1 mt-1 mb-2 rounded-lg border border-ash-3 px-3 py-2 text-sm text-onyx focus:outline-none"
							/>
						)}
						<FlatList
							data={visibleOptions}
							keyExtractor={(item) => item.value}
							showsVerticalScrollIndicator={false}
							keyboardShouldPersistTaps="handled"
							ListEmptyComponent={
								searchable ? (
									<Text className="px-4 py-3 text-sm text-ash-1">
										{noResultsLabel}
									</Text>
								) : null
							}
							renderItem={({ item }) => (
								<Pressable
									onPress={() => { onChange(item.value); closeDropdown(); }}
									className={`px-4 py-3 rounded-xl hover:bg-ash-3 ${item.value === value ? "bg-ash-3" : "bg-chalk"}`}
								>
									<Text className="text-onyx">{item.label}</Text>
								</Pressable>
							)}
						/>
					</Pressable>
				</Pressable>
			</Modal>
		</>
	);
};

export default ScDropdown;
